import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Calendar, 
  Clock, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle,
  ArrowLeft,
  Mail,
  MessageSquare,
  Target,
  Users
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { teamsService } from "@/services/teamsService";
import { wellnessService } from "@/services/wellnessService";
import { organizationsService } from "@/services/organizationsService";
import { meetingsService } from "@/services/meetingsService";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface OneOnOneInsight {
  teamMemberId: string;
  memberName: string;
  memberAvatar?: string;
  memberEmail?: string;
  recentMood: "great" | "good" | "okay" | "low" | "struggling";
  keyTopics: string[];
  suggestedQuestions: string[];
  priority: "high" | "medium" | "low";
  lastCheckIn: string;
  summary: string;
  dataInsights?: string[];
  recommendedActions?: string[];
  trends?: {
    moodTrend: "improving" | "declining" | "stable";
    energyTrend: "increasing" | "decreasing" | "stable";
  };
  metrics?: {
    avgMood: string;
    avgEnergy: string;
    avgFocus: string;
    consistencyScore: string;
    daysSinceLastCheckIn: number;
  };
}

const FullOneOnOnePrepPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  
  const [insights, setInsights] = useState<OneOnOneInsight[]>([]);
  const [selectedMember, setSelectedMember] = useState<OneOnOneInsight | null>(null);
  const [customNotes, setCustomNotes] = useState<string>("");
  const [meetingDuration, setMeetingDuration] = useState<string>("30");
  const [isLoading, setIsLoading] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  useEffect(() => {
    loadFullOneOnOneData();
  }, [user?.teamId, user?.organizationId]);

  const loadFullOneOnOneData = async () => {
    if (!user?.id) return;


    setIsLoading(true);
    try {
      // First, get team members based on user's team or organization
      let teamMemberIds: string[] = [];
      
      if (user.teamId) {
        // Get team members via teamsService
        const membersResponse = await teamsService.getMembers(user.teamId);
        if (membersResponse && membersResponse.success && membersResponse.data) {
          teamMemberIds = membersResponse.data
            .filter((m: any) => (m.id || m.userId) !== user.id)
            .map((m: any) => m.id || m.userId);
        }
      } else if (user.organizationId) {
        // Get organization members via organizationsService
        const membersResponse = await organizationsService.getMembers(user.organizationId);
        if (membersResponse && membersResponse.success && membersResponse.data) {
          teamMemberIds = membersResponse.data
            .filter((m: any) => (m.id || m.userId) !== user.id)
            .map((m: any) => m.id || m.userId);
        }
      }

      if (teamMemberIds.length === 0) {
        setInsights([]);
        return;
      }

      // Get comprehensive team member data from check-ins
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      // Get check-ins for team members
      const checkInsPromises = teamMemberIds.map((memberId: string) => 
        wellnessService.getCheckIns({
          startDate: sevenDaysAgo.toISOString(),
          limit: 50,
          userId: memberId,
        })
      );
      
      const checkInsResponses = await Promise.all(checkInsPromises);
      const checkInsData = checkInsResponses.flatMap((response: any) => 
        Array.isArray(response) ? response : (response?.data || [])
      );

      // Get user profiles from team/organization members response (already includes profile data)
      const members = user.teamId 
        ? (await teamsService.getMembers(user.teamId))?.data || []
        : (await organizationsService.getMembers(user.organizationId))?.data || [];
      
      const userProfiles = members.map((m: any) => ({
        id: m.id || m.userId,
        display_name: m.name || m.displayName,
        email: m.email,
        avatar_url: m.avatarUrl || m.avatar_url,
      }));

      // Combine check-ins with profile data
      const teamData = checkInsData.map((checkIn: any) => {
        const userId = checkIn.userId || checkIn.user_id;
        return {
          ...checkIn,
          user_id: userId,
          profiles: userProfiles.find((p: any) => p.id === userId) || {
            id: userId,
            display_name: `Team Member ${userId.slice(-4)}`,
            email: null,
            avatar_url: null
          }
        };
      });

      // Generate comprehensive insights for all team members
      const insights = await generateFullInsights(teamData || [], userProfiles);
      setInsights(insights);
      
      // Auto-select first high priority member
      const highPriorityMember = insights.find(i => i.priority === 'high');
      if (highPriorityMember) {
        setSelectedMember(highPriorityMember);
      } else if (insights.length > 0) {
        setSelectedMember(insights[0]);
      }
    } catch (error) {
      console.error('Error loading full 1:1 data:', error);
      toast.error('Failed to load team data');
      // Set empty insights on error to prevent infinite loading
      setInsights([]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateFullInsights = async (teamData: any[], allTeamProfiles: any[]): Promise<OneOnOneInsight[]> => {
    // First, create entries for all team members
    const memberData = allTeamProfiles.reduce((acc, profile) => {
      acc[profile.id] = {
        memberName: profile.display_name || profile.email?.split('@')[0] || `Team Member ${profile.id.slice(-4)}`,
        memberEmail: profile.email,
        memberAvatar: profile.avatar_url,
        checkIns: []
      };
      return acc;
    }, {} as any);

    // Then add check-in data for members who have them
    teamData.forEach(checkIn => {
      const memberId = checkIn.user_id;
      if (memberData[memberId]) {
        memberData[memberId].checkIns.push(checkIn);
      }
    });

    const insights: OneOnOneInsight[] = [];

    for (const [memberId, data] of Object.entries(memberData) as [string, any][]) {
      const { memberName, memberEmail, memberAvatar, checkIns } = data;
      
      // Handle team members without recent check-ins
      if (checkIns.length === 0) {
        insights.push({
          teamMemberId: memberId,
          memberName,
          memberEmail,
          memberAvatar,
          recentMood: "okay" as const,
          keyTopics: ["No recent check-ins"],
          suggestedQuestions: [
            "How have things been going lately?",
            "Is there anything preventing you from doing check-ins?",
            "How can I better support you?"
          ],
          priority: "high" as const,
          lastCheckIn: "Never",
          summary: `${memberName} hasn't checked in recently. Consider reaching out to ensure they're engaged and supported.`,
          dataInsights: ["No recent data available"],
          recommendedActions: ["Schedule a 1:1 meeting", "Send a check-in message"],
          metrics: {
            avgMood: "N/A",
            avgEnergy: "N/A", 
            avgFocus: "N/A",
            consistencyScore: "0%",
            daysSinceLastCheckIn: 0
          }
        });
        continue;
      }

      // Calculate comprehensive metrics
      const sortedCheckIns = checkIns.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const recentCheckIn = sortedCheckIns[0];
      
      const avgMood = checkIns.reduce((sum: number, c: any) => sum + (c.mood || 0), 0) / checkIns.length;
      const avgEnergy = checkIns.reduce((sum: number, c: any) => sum + (c.energy || 0), 0) / checkIns.length;
      const avgFocus = checkIns.reduce((sum: number, c: any) => sum + (c.focus || 0), 0) / checkIns.length;

      // Trend analysis
      const midPoint = Math.floor(checkIns.length / 2);
      const recentHalf = sortedCheckIns.slice(0, midPoint);
      const earlierHalf = sortedCheckIns.slice(midPoint);
      
      const recentAvgMood = recentHalf.length > 0 ? recentHalf.reduce((sum: number, c: any) => sum + (c.mood || 0), 0) / recentHalf.length : avgMood;
      const earlierAvgMood = earlierHalf.length > 0 ? earlierHalf.reduce((sum: number, c: any) => sum + (c.mood || 0), 0) / earlierHalf.length : avgMood;
      const moodTrend = recentAvgMood - earlierAvgMood;
      
      const recentAvgEnergy = recentHalf.length > 0 ? recentHalf.reduce((sum: number, c: any) => sum + (c.energy || 0), 0) / recentHalf.length : avgEnergy;
      const earlierAvgEnergy = earlierHalf.length > 0 ? earlierHalf.reduce((sum: number, c: any) => sum + (c.energy || 0), 0) / earlierHalf.length : avgEnergy;
      const energyTrend = recentAvgEnergy - earlierAvgEnergy;

      // Content analysis
      const allBlockers = checkIns.filter((c: any) => c.blockers && c.blockers.trim() !== "");
      const allWins = checkIns.filter((c: any) => c.wins && c.wins.trim() !== "");
      const helpRequests = checkIns.filter((c: any) => c.help_needed);

      // Priority calculation
      let priority: "high" | "medium" | "low" = "medium";
      const daysSinceLastCheckIn = Math.floor((Date.now() - new Date(recentCheckIn?.date).getTime()) / (1000 * 60 * 60 * 24));
      
      if (avgMood < 3 || allBlockers.length > 2 || helpRequests.length > 1 || daysSinceLastCheckIn > 3) {
        priority = "high";
      } else if (avgMood > 4 && allBlockers.length === 0) {
        priority = "low";
      }

      // Generate key topics and questions
      const keyTopics: string[] = [];
      const suggestedQuestions: string[] = [];
      const dataInsights: string[] = [];
      const recommendedActions: string[] = [];

      // Mood-based insights
      if (avgMood < 3) {
        keyTopics.push("Wellbeing Support", "Stress Management");
        suggestedQuestions.push("How have you been feeling about work lately?");
        suggestedQuestions.push("What's been the most challenging part of your week?");
        dataInsights.push(`Below-average mood (${avgMood.toFixed(1)}/5) needs attention`);
        recommendedActions.push("Offer additional support and resources");
      } else if (avgMood > 4) {
        keyTopics.push("Growth Opportunities", "Career Development");
        suggestedQuestions.push("What new challenges would you like to take on?");
        dataInsights.push(`Excellent mood (${avgMood.toFixed(1)}/5) - good time for growth discussions`);
        recommendedActions.push("Discuss career advancement opportunities");
      }

      // Blocker-based insights
      if (allBlockers.length > 0) {
        keyTopics.push("Remove Blockers", "Process Improvements");
        suggestedQuestions.push("What blockers can I help remove for you?");
        dataInsights.push(`${allBlockers.length} blockers reported recently`);
        recommendedActions.push("Work together to eliminate reported blockers");
      }

      // Energy trends
      if (energyTrend < -10) {
        keyTopics.push("Energy Management", "Workload Balance");
        suggestedQuestions.push("Have you been feeling more tired than usual?");
        dataInsights.push("Energy levels declining - may indicate burnout risk");
        recommendedActions.push("Review workload and consider adjustments");
      }

      // Wins and achievements
      if (allWins.length > 0) {
        keyTopics.push("Celebrate Wins", "Recognition");
        suggestedQuestions.push("Tell me more about your recent wins!");
        dataInsights.push(`${allWins.length} wins shared - positive engagement`);
        recommendedActions.push("Acknowledge and celebrate achievements");
      }

      // Help requests
      if (helpRequests.length > 0) {
        keyTopics.push("Support Needs", "Skill Development");
        suggestedQuestions.push("What kind of support would be most helpful right now?");
        dataInsights.push(`${helpRequests.length} help requests - seeking guidance`);
        recommendedActions.push("Provide requested support and mentoring");
      }

      // Default topics if none identified
      if (keyTopics.length === 0) {
        keyTopics.push("General Check-in", "Project Updates", "Team Collaboration");
        suggestedQuestions.push("How are things going with your current projects?");
        suggestedQuestions.push("What support do you need from me?");
      }

      // Default questions if none identified
      if (suggestedQuestions.length < 3) {
        suggestedQuestions.push("What's been going well for you?");
        suggestedQuestions.push("Any feedback on team processes?");
      }

      insights.push({
        teamMemberId: memberId,
        memberName: memberName || 'Unknown Member',
        memberEmail,
        memberAvatar,
        recentMood: avgMood >= 4.5 ? "great" : avgMood >= 4 ? "good" : avgMood >= 3 ? "okay" : avgMood >= 2 ? "low" : "struggling",
        keyTopics: keyTopics.slice(0, 4),
        suggestedQuestions: suggestedQuestions.slice(0, 5),
        priority,
        lastCheckIn: recentCheckIn.date,
        summary: `${memberName} has ${avgMood < 3 ? "been struggling" : avgMood > 4 ? "been performing well" : "shown mixed patterns"} based on recent check-ins. ${allBlockers.length > 0 ? `Currently facing ${allBlockers.length} blocker(s).` : ""} ${helpRequests.length > 0 ? `Seeking help with ${helpRequests.length} area(s).` : ""}`,
        dataInsights,
        recommendedActions,
        trends: {
          moodTrend: moodTrend > 0.3 ? "improving" : moodTrend < -0.3 ? "declining" : "stable",
          energyTrend: energyTrend > 5 ? "increasing" : energyTrend < -5 ? "decreasing" : "stable"
        },
        metrics: {
          avgMood: avgMood.toFixed(1),
          avgEnergy: avgEnergy.toFixed(1),
          avgFocus: avgFocus.toFixed(1),
          consistencyScore: checkIns.length >= 5 ? "high" : checkIns.length >= 3 ? "medium" : "low",
          daysSinceLastCheckIn
        }
      });
    }

    return insights.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  const handleScheduleMeeting = async () => {
    if (!selectedMember || !user?.id) return;

    setIsScheduling(true);
    try {
      // Calculate meeting time (default to tomorrow at 2 PM, duration from state)
      const duration = parseInt(meetingDuration) || 30;
      const meetingStartTime = new Date();
      meetingStartTime.setDate(meetingStartTime.getDate() + 1); // Tomorrow
      meetingStartTime.setHours(14, 0, 0, 0); // 2 PM
      
      const meetingEndTime = new Date(meetingStartTime);
      meetingEndTime.setMinutes(meetingEndTime.getMinutes() + duration);

      const response = await meetingsService.createMeeting({
        title: `1:1 with ${selectedMember.memberName}`,
        description: selectedMember.keyTopics.length > 0 
          ? `Topics to discuss: ${selectedMember.keyTopics.join(', ')}`
          : undefined,
        participantIds: [user.id, selectedMember.teamMemberId],
        startTime: meetingStartTime.toISOString(),
        endTime: meetingEndTime.toISOString(),
        type: 'one_on_one',
      });

      if (response?.data) {
        toast.success(`Meeting scheduled with ${selectedMember.memberName} for ${meetingStartTime.toLocaleDateString()} at ${meetingStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}!`);
      } else {
        toast.error('Failed to schedule meeting');
      }
    } catch (error: any) {
      console.error('Error scheduling meeting:', error);
      toast.error(error?.response?.data?.message || 'Failed to schedule meeting');
    } finally {
      setIsScheduling(false);
    }
  };

  const getMoodIcon = (mood: string) => {
    switch (mood) {
      case "low":
      case "struggling":
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      case "good":
      case "great":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "border-red-200 bg-red-50 text-red-800";
      case "medium":
        return "border-amber-200 bg-amber-50 text-amber-800";
      case "low":
        return "border-green-200 bg-green-50 text-green-800";
      default:
        return "border-gray-200 bg-gray-50 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold">Loading 1:1 Preparation...</h1>
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold">🎯 1:1 Preparation Center</h1>
            <p className="text-muted-foreground">AI-powered insights for meaningful team conversations</p>
          </div>
        </div>
        <Badge variant="outline" className="text-sm">
          {insights.length} team members
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Members List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Members
              </CardTitle>
              <CardDescription>
                Select a team member to view detailed preparation insights
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {insights.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No team members found.</p>
                </div>
              ) : (
                insights.map((insight) => (
                <div
                  key={insight.teamMemberId}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedMember?.teamMemberId === insight.teamMemberId
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedMember(insight)}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={insight.memberAvatar} alt={insight.memberName} />
                      <AvatarFallback>{insight.memberName?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm truncate">{insight.memberName}</h4>
                        {getMoodIcon(insight.recentMood)}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getPriorityColor(insight.priority)}>
                          {insight.priority}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {insight.lastCheckIn === "Never" ? "Never" : `${insight.metrics?.daysSinceLastCheckIn}d ago`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detailed Insights */}
        <div className="lg:col-span-2">
          {selectedMember ? (
            <div className="space-y-6">
              {/* Member Overview */}
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={selectedMember.memberAvatar} alt={selectedMember.memberName} />
                        <AvatarFallback className="text-lg">{selectedMember.memberName?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h2 className="text-2xl font-bold">{selectedMember.memberName}</h2>
                        <p className="text-muted-foreground">{selectedMember.memberEmail}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {getMoodIcon(selectedMember.recentMood)}
                          <span className="text-sm font-medium capitalize">{selectedMember.recentMood}</span>
                          <Badge className={getPriorityColor(selectedMember.priority)}>
                            {selectedMember.priority} priority
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={handleScheduleMeeting}
                      disabled={isScheduling}
                      className="gap-2"
                    >
                      <Calendar className="h-4 w-4" />
                      {isScheduling ? 'Scheduling...' : 'Schedule Meeting'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{selectedMember.metrics?.avgMood}/5</div>
                      <div className="text-xs text-muted-foreground">Avg Mood</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{selectedMember.metrics?.avgEnergy}%</div>
                      <div className="text-xs text-muted-foreground">Avg Energy</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{selectedMember.metrics?.avgFocus}%</div>
                      <div className="text-xs text-muted-foreground">Avg Focus</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold capitalize">{selectedMember.metrics?.consistencyScore}</div>
                      <div className="text-xs text-muted-foreground">Consistency</div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2">📋 AI Summary</h4>
                    <p className="text-sm text-muted-foreground">{selectedMember.summary}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Key Topics & Questions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">🎯 Key Topics to Discuss</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedMember.keyTopics.map((topic, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">💬 Suggested Questions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {selectedMember.suggestedQuestions.map((question, index) => (
                        <div key={index} className="p-2 bg-blue-50 rounded border-l-4 border-blue-500">
                          <p className="text-sm italic">"{question}"</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Data Insights & Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {selectedMember.dataInsights && selectedMember.dataInsights.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">📊 Data Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedMember.dataInsights.map((insight, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            <span className="text-sm">{insight}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {selectedMember.recommendedActions && selectedMember.recommendedActions.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">⚡ Recommended Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedMember.recommendedActions.map((action, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <Target className="h-4 w-4 text-green-500 mt-0.5" />
                            <span className="text-sm">{action}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Meeting Preparation */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">📝 Meeting Preparation</CardTitle>
                  <CardDescription>
                    Customize your meeting plan and add personal notes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Meeting Duration</label>
                    <Select value={meetingDuration} onValueChange={setMeetingDuration}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Personal Notes</label>
                    <Textarea
                      placeholder="Add your own notes, observations, or custom topics to discuss..."
                      value={customNotes}
                      onChange={(e) => setCustomNotes(e.target.value)}
                      rows={4}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      onClick={handleScheduleMeeting}
                      disabled={isScheduling}
                      className="flex-1"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      {isScheduling ? 'Scheduling...' : 'Schedule Meeting'}
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <Mail className="h-4 w-4 mr-2" />
                      Email Agenda
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="h-96 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Select a Team Member</h3>
                <p>Choose a team member from the list to view detailed 1:1 preparation insights</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullOneOnOnePrepPage;